import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { inr } from "@/lib/format";
import { useStore } from "@/context/StoreContext";

export default function PriceBreakup({ breakup, surcharge = 0, gstRate = 3, rateAt, personalisationLabel, stone, colour }) {
  const [open, setOpen] = useState(false);
  const { config } = useStore();
  const showRate = (config?.rate_strip_locations || ["footer", "coins_home", "gold_home", "product_breakup"]).includes("product_breakup");
  if (!breakup) return null;

  const pers = surcharge || breakup.personalisation || 0;
  const subtotal = (breakup.subtotal || 0) + (surcharge ? surcharge : 0) - (surcharge ? (breakup.personalisation || 0) : 0);
  const gst = Math.round(((subtotal * gstRate) / 100) * 100) / 100;
  const final = Math.round((subtotal + gst) * 100) / 100;

  const metalLabel = `Metal — ${breakup.purity} ${breakup.metal === "silver" ? "silver" : "gold"}${colour ? ` ${colour.toLowerCase()}` : ""} · ${breakup.net_weight}g × ${inr(breakup.purity_rate_per_gram)}/g`;

  const rows = breakup.overridden
    ? [["Manual price", inr(breakup.subtotal - (breakup.personalisation || 0))]]
    : [
        [metalLabel, inr(breakup.metal_value)],
        ...(breakup.wastage_value ? [[`Wastage (${breakup.wastage_pct}%)`, inr(breakup.wastage_value)]] : []),
        ...(stone && breakup.stone_value
          ? [[`Diamond — ${stone.type} · ${stone.carats} ct × ${inr(stone.rate)}/ct`, inr(breakup.stone_value)]]
          : breakup.stone_value ? [["Stones & diamonds", inr(breakup.stone_value)]] : []),
        [`Making charges — ${breakup.making_detail}`, inr(breakup.making)],
        ...(breakup.hallmark_charge ? [["Hallmarking charges", inr(breakup.hallmark_charge)]] : []),
        ...(breakup.certification_charge ? [["Certification charges", inr(breakup.certification_charge)]] : []),
      ];

  return (
    <div className="border border-stone-200 bg-white rounded-lg" data-testid="price-breakup">
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-stone-50 transition-colors"
        onClick={() => setOpen(!open)}
        data-testid="price-breakup-toggle"
        aria-expanded={open}
      >
        <span>View price breakup</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="px-4 pb-4 animate-fade-up">
          <dl className="space-y-1.5 text-sm">
            {rows.map(([label, val]) => (
              <div key={label} className="flex justify-between gap-3">
                <dt className="text-stone-500 text-xs sm:text-sm">{label}</dt>
                <dd className="font-mono text-xs sm:text-sm shrink-0">{val}</dd>
              </div>
            ))}
            {pers > 0 && (
              <div className="flex justify-between gap-3">
                <dt className="text-stone-500 text-xs sm:text-sm">Personalisation{personalisationLabel ? ` — ${personalisationLabel}` : ""}</dt>
                <dd className="font-mono text-xs sm:text-sm shrink-0">{inr(pers)}</dd>
              </div>
            )}
            <div className="flex justify-between border-t border-stone-200 pt-1.5">
              <dt className="text-stone-600 text-xs sm:text-sm">Sub-total</dt>
              <dd className="font-mono text-xs sm:text-sm">{inr(subtotal)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-stone-500 text-xs sm:text-sm">GST @ {gstRate}%</dt>
              <dd className="font-mono text-xs sm:text-sm">{inr(gst)}</dd>
            </div>
            <div className="flex justify-between border-t border-stone-900 pt-2 font-semibold">
              <dt>Grand total</dt>
              <dd className="font-mono" data-testid="breakup-final">{inr(final)}</dd>
            </div>
          </dl>
          {rateAt && showRate && (
            <p className="text-[11px] font-mono text-stone-500 mt-3" data-testid="rate-as-on">
              Gold rate applied: {breakup.purity} {inr(breakup.purity_rate_per_gram)}/g · as on{" "}
              {new Date(rateAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
            </p>
          )}
          <p className="text-[11px] text-stone-500 mt-2 leading-relaxed">
            Our prices breathe with the live bullion rate — what you see is computed from today's rate, never a stale tag.
            Once a piece is in your cart, its price is held for you while you decide.
          </p>
        </div>
      )}
    </div>
  );
}
